import { RootState } from '@Common/models/redux/store';
import React, { memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, Button } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { useRouter } from 'next/router';
import { CoWriterLoader } from '../common/Loader';
import { Loadable } from '../common/Loadable';
import { IsVisible } from '../common/IsVisible';
import { TextDialogWithCircularProgress } from '../common';

export type NotSupportedPageOwnProps = {
  message: string;
};

export type SupportedEnvGuardOwnProps = {
  renderNotSupportedEnvPage: React.FC<any>;
  renderLoader: React.FC<any>;
  redirect?: string;
};

export type SupportedEnvGuardProps = React.PropsWithChildren<SupportedEnvGuardOwnProps>;

const useMobileStyles = makeStyles((theme) => ({
  lostImg: {
    maxWidth: '650px',
    margin: 'auto',
    width: '100%',
  },
  smallTitle: {
    fontWeight: 600,
    fontSize: '31px',
    lineHeight: 'normal',
    marginBottom: '40px',
    [theme.breakpoints.down(959)]: {
      fontSize: '18px',
    },
  },
  bigTitle: {
    fontWeight: 600,
    fontSize: '42px',
    lineHeight: '42px',
    margin: '80px 0px 40px',
    [theme.breakpoints.down(959)]: {
      fontSize: '25px',
    },
  },
  quillBtn: {
    color: '#fff',
    fontSize: '24px',
    lineHeight: '51px',
    borderRadius: '100px',
    fontWeight: 600,
    padding: '5px 40px',
    marginBottom: '60px',

    [theme.breakpoints.down(959)]: {
      fontSize: '15px',
      lineHeight: '22px',
    },
  },
}));

const products = [
  {
    name: 'Paraphraser',
    link: '/',
  },
  {
    name: 'Grammar Checker',
    link: '/grammar-check',
  },
  {
    name: 'Summarizer',
    link: '/summarize',
  },
];

function NotSupportedEnvComponent(props: NotSupportedPageOwnProps) {
  const classes = useMobileStyles();
  const router = useRouter();
  const notCurrentProduct = products.filter(({ link }) => router.pathname !== link);
  const product = notCurrentProduct[Math.floor(Math.random() * notCurrentProduct.length)];

  return (
    <div className="container" style={{ textAlign: 'center' }}>
      <h2 className={classes.bigTitle}>{props.message}</h2>
      <img alt="ErrorImage" src="/images/404Page.svg" className={classes.lostImg} />
      <h3 className={classes.smallTitle}>Meanwhile, try our</h3>
      <div>
        <Link href={product.link}>
          <Button variant="contained" color="primary" className={classes.quillBtn}>
            {product.name}
          </Button>
        </Link>
      </div>
    </div>
  );
}

export const NotSupportedEnvPage = memo(NotSupportedEnvComponent);

export function useCoWriterSupportedEnv(redirect = false) {
  const isMobile = useSelector((state: RootState) => state.common.platform.isMobile);
  const isIE = useSelector((state: RootState) => state.common.platform.isIE);

  const notSupported = isMobile || isIE;

  return {
    supported: !notSupported,
    message: isMobile
      ? 'This product is available only on desktop platform.'
      : 'You are using an old browser. Please use a modern browser.',
  };
}

export function SupportedEnvGuard(props: SupportedEnvGuardProps) {
  const router = useRouter();
  const { supported, message } = useCoWriterSupportedEnv();
  const {
    redirect = '',
    renderLoader: LoaderComponent,
    renderNotSupportedEnvPage: FallbackPageComponent,
  } = props;

  useEffect(() => {
    const validRedirectPathName = redirect?.trim?.().length > 0;
    const synced = typeof supported !== 'undefined';

    if (validRedirectPathName && synced && !supported) {
      router.replace(redirect?.trim?.());
    }
  }, [supported, redirect]);

  const notSynced = typeof supported === 'undefined';

  let loading = false;
  // show loader while data is not synced
  if (notSynced) {
    loading = true;
  } else if (!supported && redirect?.trim?.().length > 0) {
    // data gets synced, but page is not supported
    // while the page is not supported we may be waiting for redirect to happen
    // or we may show Not-Supported-Page here only
    // if case where it is not supported and redirect should happen
    // we cannot consider loading to be completed so
    loading = true;
  }

  return (
    <Loadable loading={loading} loader={<LoaderComponent />}>
      {supported ? props.children : <FallbackPageComponent message={message} />}
    </Loadable>
  );
}
